import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const TIMES = ["Morning (9–12)", "Afternoon (12–5)", "Evening (5–7)", "Any time"];

export default function BookDemo() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", preferred: "Any time", note: "", website: "" });

  useEffect(() => {
    api.get("/demo/settings").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  if (!cfg?.enabled) return null;

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/demo/request", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <button onClick={() => { setOpen(true); setDone(false); }} data-testid="book-demo-button"
        title="Book a short walkthrough of HiredToday with our team"
        className="fixed bottom-5 left-5 z-40 h-11 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-full shadow-lg hover:bg-zinc-800 hover:scale-[1.03] transition-all duration-200 print:hidden">
        {cfg.button_label || "Book a demo"}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="book-demo-modal" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-zinc-950/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-xl border border-zinc-200 shadow-2xl w-full max-w-md p-6 sm:p-8">
            <button onClick={() => setOpen(false)} data-testid="book-demo-close" aria-label="Close"
              className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-900 transition-colors duration-200">
              <X size={18} />
            </button>
            {done ? (
              <div className="text-center py-6" data-testid="book-demo-success">
                <p className="font-display text-xl font-bold text-zinc-950">Thank you, request received</p>
                <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
                  We've emailed you a confirmation and we'll be in touch shortly to fix a time, usually within one working day.
                </p>
                <button onClick={() => setOpen(false)} data-testid="book-demo-done-close"
                  className="mt-6 h-10 px-6 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="font-display text-xl font-bold text-zinc-950" data-testid="book-demo-heading">{cfg.heading}</p>
                {cfg.subtext && <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{cfg.subtext}</p>}
                <div className="mt-5 space-y-3">
                  <input value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="demo-name"
                    className="w-full h-10 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                  <input value={form.email} onChange={set("email")} type="email" placeholder="Work email *" data-testid="demo-email"
                    className="w-full h-10 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                  <input value={form.company} onChange={set("company")} placeholder="Company (optional)" data-testid="demo-company"
                    className="w-full h-10 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                  <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="demo-phone"
                    className="w-full h-10 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                  <select value={form.preferred} onChange={set("preferred")} data-testid="demo-preferred"
                    className="w-full h-10 px-3 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:border-zinc-900">
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <textarea value={form.note} onChange={set("note")} rows={2} placeholder="Anything you'd like us to cover? (optional)" data-testid="demo-note"
                    className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                  <input value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true"
                    className="absolute opacity-0 pointer-events-none h-0 w-0" />
                  <button onClick={submit} disabled={sending} data-testid="demo-submit"
                    className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                    {sending ? "Sending…" : "Request my demo"}
                  </button>
                  <p className="text-[11px] text-zinc-400 text-center">We'll only use your details to arrange the demo.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
